import React, { createContext, useContext, useState, useEffect } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasCheckedInitialSession = React.useRef(false);

  useEffect(() => {
    // Timeout de segurança para garantir que o loading não fica infinito
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 5000);

    // Setup do listener de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, currentSession) => {
      // ESTRATÉGIA RESILIENTE: só redirecionar em eventos de logout real
      // TOKEN_REFRESHED e INITIAL_SESSION podem ter currentSession null transitoriamente
      if (event === "SIGNED_OUT") {
        setSession(null);
        setUser(null);
        setIsLoading(false);
        clearTimeout(loadingTimeout);
        // ProtectedRoute fará o redirect via React Router (SPA),
        // evitando o full reload que causava tela branca.
        return;
      }


      // Para outros eventos, atualizar estado SOMENTE se currentSession não for null
      // Isso evita "piscar" o user/session durante refreshes
      if (currentSession) {
        setSession(currentSession);
        setUser(currentSession.user);
      }

      setIsLoading(false);
      clearTimeout(loadingTimeout);
    });

    // Verifica sessão existente
    const checkInitialSession = async () => {
      try {
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession();

        // Atualiza estado com a sessão encontrada (ou null)
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        hasCheckedInitialSession.current = true;
      } catch (error) {
        // Mesmo com erro, limpa o estado
        setSession(null);
        setUser(null);
        hasCheckedInitialSession.current = true;
      } finally {
        // SEMPRE reseta o loading, com ou sem erro
        setIsLoading(false);
        clearTimeout(loadingTimeout);
      }
    };

    checkInitialSession();

    // Mantém a sessão viva agressivamente:
    // - refresh silencioso a cada 5 minutos
    // - refresh ao focar a aba
    // - refresh quando volta online
    const refresh = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data?.session) return;
        // Force refresh to keep session alive
        await supabase.auth.refreshSession();
        console.log("🔄 AuthProvider: Sessão renovada automaticamente");
      } catch (e) {
        console.warn("⚠️ Auth refresh warning:", e);
      }
    };

    // Intervalo menor: 5 minutos ao invés de 15
    const refreshInterval = setInterval(refresh, 1000 * 60 * 5); // 5 min

    const onFocus = () => {
      console.log("👁️ AuthProvider: Aba focada, renovando sessão");
      refresh();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("👁️ AuthProvider: Aba visível, renovando sessão");
        refresh();
      }
    };
    const onOnline = () => {
      console.log("🌐 AuthProvider: Voltou online, renovando sessão");
      refresh();
    };

    window.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);

    return () => {
      console.log("🧹 AuthProvider: Limpando subscription");
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
      clearInterval(refreshInterval);
      window.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  // Log sempre que o estado de loading mudar
  useEffect(() => {
    console.log("📊 AuthProvider: isLoading =", isLoading);
  }, [isLoading]);

  useEffect(() => {
    console.log("👤 AuthProvider: user =", user?.email || "null");
  }, [user]);

  const value = {
    user,
    session,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
